import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  Progress,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';

interface Task {
  projectTaskId: string;
  titleAr: string;
  status: number;
  priority: number;
  dueDate: string | null;
}
interface Project {
  projectId: string;
  nameAr: string;
  status: number;
  taskCount: number;
  doneCount: number;
  tasks: Task[];
}

const STATUS_COLOR: Record<number, string> = { 1: 'blue', 2: 'green', 3: 'gray' };

export function ProjectsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [openProject, setOpenProject] = useState<Project | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['projects'] as const,
    queryFn: async () => (await apiClient.get<Project[]>('/api/projects')).data,
  });

  const statusLabel = (s: number) => t(`projects.status.${s === 1 ? 'open' : s === 2 ? 'completed' : 'cancelled'}`);

  return (
    <Stack gap="lg">
      <PageHeader eyebrow={t('nav.sections.operations')} title={t('projects.title')}
        subtitle={t('projects.subtitle')} />
      <Group justify="flex-end">
        <Button color="wheels" leftSection={<IconPlus size={16} stroke={1.8} />}
          onClick={() => setCreateOpen(true)}>{t('projects.new')}</Button>
      </Group>

      <Paper p="md">
        {isLoading ? <Group justify="center" py="lg"><Loader color="wheelsRed" /></Group>
          : (data ?? []).length === 0 ? <Text size="sm" c="dimmed" ta="center" py="lg">{t('projects.empty')}</Text>
          : (
          <Table verticalSpacing="sm">
            <Table.Thead><Table.Tr>
              <Table.Th>{t('projects.name')}</Table.Th>
              <Table.Th>{t('projects.progress')}</Table.Th>
              <Table.Th>{t('projects.statusCol')}</Table.Th>
              <Table.Th />
            </Table.Tr></Table.Thead>
            <Table.Tbody>
              {(data ?? []).map((p) => (
                <Table.Tr key={p.projectId}>
                  <Table.Td>{p.nameAr}</Table.Td>
                  <Table.Td style={{ width: 220 }}>
                    <Group gap="xs">
                      <Progress value={p.taskCount ? (p.doneCount / p.taskCount) * 100 : 0}
                        color="wheels" w={120} />
                      <Text size="xs" c="dimmed" className="font-numeric">{p.doneCount}/{p.taskCount}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td><Badge variant="light" color={STATUS_COLOR[p.status]}>{statusLabel(p.status)}</Badge></Table.Td>
                  <Table.Td><Group justify="flex-end">
                    <Button size="xs" variant="default" onClick={() => setOpenProject(p)}>{t('projects.tasks')}</Button>
                  </Group></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <ProjectModal opened={createOpen} onClose={() => setCreateOpen(false)}
        onSaved={() => void qc.invalidateQueries({ queryKey: ['projects'] })} />
      <TasksModal project={openProject} onClose={() => setOpenProject(null)}
        onChanged={() => void qc.invalidateQueries({ queryKey: ['projects'] })} />
    </Stack>
  );
}

function ProjectModal({ opened, onClose, onSaved }: { opened: boolean; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const save = useMutation({
    mutationFn: async () => apiClient.post('/api/projects',
      { nameAr: name.trim(), nameEn: null, customerId: null, status: 1, startDate: null, endDate: null, notes: notes.trim() || null }),
    onSuccess: () => { setName(''); setNotes(''); onSaved(); onClose(); },
    onError: () => notifications.show({ color: 'red', title: t('common.toasts.errorTitle'), message: '' }),
  });
  return (
    <Modal opened={opened} onClose={onClose} title={t('projects.new')}>
      <Stack gap="sm">
        <TextInput label={t('projects.name')} value={name} onChange={(e) => setName(e.currentTarget.value)} withAsterisk />
        <Textarea label={t('projects.notes')} value={notes} onChange={(e) => setNotes(e.currentTarget.value)} autosize minRows={2} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>{t('common.cancel')}</Button>
          <Button color="wheels" loading={save.isPending} disabled={!name.trim()} onClick={() => save.mutate()}>{t('common.save')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function TasksModal({ project, onClose, onChanged }: { project: Project | null; onClose: () => void; onChanged: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('2');

  const addTask = useMutation({
    mutationFn: async () => apiClient.post(`/api/projects/${project!.projectId}/tasks`,
      { titleAr: title.trim(), status: 1, priority: Number(priority), assigneeUserId: null, dueDate: null, notes: null }),
    onSuccess: () => { setTitle(''); onChanged(); void qc.invalidateQueries({ queryKey: ['projects'] }); },
  });
  const setStatus = useMutation({
    mutationFn: async ({ task, status }: { task: Task; status: number }) =>
      apiClient.put(`/api/projects/${project!.projectId}/tasks/${task.projectTaskId}`,
        { titleAr: task.titleAr, status, priority: task.priority, assigneeUserId: null, dueDate: task.dueDate, notes: null }),
    onSuccess: () => { onChanged(); },
  });

  return (
    <Modal opened={project !== null} onClose={onClose} title={project?.nameAr} size="lg">
      <Stack gap="sm">
        {(project?.tasks ?? []).map((task) => (
          <Group key={task.projectTaskId} justify="space-between"
            className="rounded-md border border-[var(--wheels-border)] bg-white" px="sm" py={6}>
            <Text size="sm" td={task.status === 3 ? 'line-through' : undefined}>{task.titleAr}</Text>
            <Select size="xs" w={130} value={String(task.status)}
              onChange={(v) => setStatus.mutate({ task, status: Number(v) })}
              data={[
                { value: '1', label: t('projects.taskStatus.todo') },
                { value: '2', label: t('projects.taskStatus.inProgress') },
                { value: '3', label: t('projects.taskStatus.done') },
              ]} />
          </Group>
        ))}
        <Group gap="sm" align="flex-end">
          <TextInput className="flex-1" label={t('projects.addTask')} value={title}
            onChange={(e) => setTitle(e.currentTarget.value)} />
          <Select w={120} label={t('projects.priority')} value={priority} onChange={(v) => setPriority(v ?? '2')}
            data={[
              { value: '1', label: t('projects.prio.low') },
              { value: '2', label: t('projects.prio.medium') },
              { value: '3', label: t('projects.prio.high') },
            ]} />
          <Button color="wheels" loading={addTask.isPending} disabled={!title.trim()} onClick={() => addTask.mutate()}>
            {t('common.add')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
